import { ChangeDetectionStrategy, Component, DestroyRef, NgZone, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { RouterOutlet } from '@angular/router';
import { PrimeNGConfig } from 'primeng/api';
import { DialogService, DynamicDialogModule, DynamicDialogRef } from 'primeng/dynamicdialog';
import { ProgressBarModule } from 'primeng/progressbar';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { ToastModule } from 'primeng/toast';

import { LoaderService } from './core/service/loader.service';
import { FcmDeviceRegistrationService } from './core/service/fcm-device-registration.service';
import { messaging } from '../configs/firebase.config';
import { NotificationPayload } from './shared/models/INotification';
import { ToastService } from './core/service/toast.service';
import { OrderNotifyModalComponent } from './shared/components/order-notify-modal/order-notify-modal.component';
import { PushNotificationType } from './shared/enum.ts/push-notification-type.enum';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [CommonModule, RouterOutlet, DynamicDialogModule, ProgressBarModule, ProgressSpinnerModule, ToastModule],
  providers: [DialogService],
  templateUrl: './app.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AppComponent implements OnInit {
  private readonly primengConfig = inject(PrimeNGConfig);
  readonly loaderService = inject(LoaderService);
  private readonly toastService = inject(ToastService);
  private readonly zone = inject(NgZone);
  private readonly dialogService = inject(DialogService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly fcmDeviceRegistration = inject(FcmDeviceRegistrationService);

  readonly loaderState = toSignal(this.loaderService.getLoaderState(), { initialValue: false });
  ref: DynamicDialogRef | undefined;

  ngOnInit(): void {
    this.primengConfig.ripple = true;
    this.setupForegroundMessaging();
    this.fcmDeviceRegistration.tryRegisterWebPush();
  }

  private setupForegroundMessaging(): void {
    if (!messaging) return;
    messaging.onMessage((incomingMessage: NotificationPayload) => {
      this.zone.run(() => {
        const title = incomingMessage.notification?.title ?? '';
        const body = incomingMessage.notification?.body ?? '';
        this.toastService.showSuccess(title, body);
        const t = incomingMessage.data?.type;
        if (t === PushNotificationType.STAFF_NEW_ORDER || t === 'order') {
          this.openOrderNotifyModal({ ...incomingMessage.notification, ...incomingMessage.data });
        }
      });
    });
  }

  openOrderNotifyModal(order: any): void {
    this.ref = this.dialogService.open(OrderNotifyModalComponent, {
      header: 'Order Created',
      width: '30vw',
      height: '50%',
      modal: true,
      data: {
        order: order,
      },
    });
    this.ref.onClose.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => {},
    });
  }
}
